import React, { useCallback, useRef } from 'react';
import EditIcon from '@atlaskit/icon/core/edit';
import { IconButton } from '@atlaskit/button/new';
import { Box } from '@atlaskit/primitives';
import Tooltip from '@atlaskit/tooltip';
import { getAriConfig, isAri } from '@atlassian/jira-platform-ari/src/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { asyncInlineEditStatusModal } from '@atlassian/jira-inline-edit-status-modal/entrypoint.tsx';
import { defaultModalProps } from '@atlassian/jira-status-config-common/src/constants.tsx';
import { ModalEntryPointPressableTrigger } from '@atlassian/jira-modal-entry-point-pressable-trigger/src/ModalEntryPointPressableTrigger.tsx';
import { useProject } from '@atlassian/jira-business-entity-project-hook/src/index.tsx';
import { useWorkflows } from '@atlassian/jira-business-workflows/src/controllers/workflows-context/index.tsx';
import { LIST_VIEW_GROUP_BY_STATUS_SOURCE } from '@atlassian/jira-status-config-common/src/types.tsx';
import { statusCategoryForId } from '@atlassian/jira-common-constants/src/status-categories.tsx';
import { useIntl } from '@atlassian/jira-intl';
import type {
	WorkflowDetails,
	WorkflowTransition,
} from '@atlassian/jira-business-workflows/src/services/workflows/types.tsx';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { editStatusExperience } from '../../../../../experiences.tsx';
import { useListViewRefreshCacheActions } from '../../../../../controllers/list-view-refresh-cache/ListViewRefreshCacheStore.tsx';
import { messages } from './messages.tsx';

type EditStatusButtonProps = {
	status: { id?: string; name: string; categoryId: string } | undefined;
};
export const getStatusIdFromARI = (statusARI: string) => {
	const { resourceId } = getAriConfig(statusARI);
	return resourceId;
};

export const getIssueTypeIdForStatus = (
	workflows: WorkflowDetails[] | undefined,
	statusId: string,
) => {
	const workflowWithStatusId = workflows?.find((workflow: WorkflowDetails) =>
		workflow.transitions.some(
			(transition: WorkflowTransition) => transition.to.statusId === statusId,
		),
	);
	return workflowWithStatusId?.issueTypes[0]?.issueTypeId || '';
};

export const EditStatusButton = ({ status }: EditStatusButtonProps) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { formatMessage } = useIntl();
	const editButtonRef = useRef<HTMLButtonElement>(null);
	const project = useProject();
	const workflows: WorkflowDetails[] | undefined = useWorkflows();
	const hasMultipleWorkflows = workflows ? workflows?.length > 1 : false;
	const issueTypeId = workflows ? workflows[0].issueTypes[0].issueTypeId : '';

	const { triggerListViewCacheRefresh } = fg('jira-list-view-trigger-refresh-action')
		? // eslint-disable-next-line react-hooks/rules-of-hooks
			useListViewRefreshCacheActions()
		: { triggerListViewCacheRefresh: undefined };
	const { triggerListViewGroupByCacheRefresh } = fg('refetch_status_on_list_group_by_status')
		? // eslint-disable-next-line react-hooks/rules-of-hooks
			useListViewRefreshCacheActions()
		: { triggerListViewGroupByCacheRefresh: undefined };

	const onStatusUpdated = useCallback(async () => {
		await triggerListViewCacheRefresh?.();
		await triggerListViewGroupByCacheRefresh?.();
	}, [triggerListViewCacheRefresh, triggerListViewGroupByCacheRefresh]);

	const onCloseEditStatusModal = useCallback(() => {
		fireUIAnalytics(
			createAnalyticsEvent({
				actionSubject: 'inlineEditStatusModal',
				action: 'closed',
			}),
			{ source: LIST_VIEW_GROUP_BY_STATUS_SOURCE },
		);
	}, [createAnalyticsEvent]);

	const handleOnEditButtonClick = useCallback(() => {
		fireUIAnalytics(
			createAnalyticsEvent({
				action: 'clicked',
				actionSubject: 'button',
			}),
			'editStatusButton',
			{
				source: LIST_VIEW_GROUP_BY_STATUS_SOURCE,
			},
		);
	}, [createAnalyticsEvent]);

	if (!status) return null;

	const statusId = status?.id && isAri(status?.id) ? getStatusIdFromARI(status?.id) : status.id;

	const entryPointProps = {
		status: {
			id: statusId || '',
			statusName: status.name,
			statusCategory: statusCategoryForId(status.categoryId),
		},
		projectId: String(project.id),
		issueTypeId:
			hasMultipleWorkflows && statusId ? getIssueTypeIdForStatus(workflows, statusId) : issueTypeId,
		projectKey: project.key,
		statusNames: [],
		source: LIST_VIEW_GROUP_BY_STATUS_SOURCE,
		editStatusExperience,
		onStatusUpdated,
		onClose: onCloseEditStatusModal,
	};
	return (
		<ModalEntryPointPressableTrigger
			entryPoint={asyncInlineEditStatusModal}
			entryPointProps={entryPointProps}
			interactionName="edit-status-modal"
			modalProps={{ ...defaultModalProps, shouldReturnFocus: editButtonRef }}
			useInternalModal
		>
			{({ ref }) => (
				<Box ref={ref}>
					<Tooltip
						content={formatMessage(messages.statusNameButtonLabel, {
							statusName: status?.name,
						})}
					>
						<IconButton
							ref={editButtonRef}
							appearance="subtle"
							icon={EditIcon}
							label={formatMessage(messages.statusNameButtonLabel, {
								statusName: status?.name,
							})}
							onClick={handleOnEditButtonClick}
						/>
					</Tooltip>
				</Box>
			)}
		</ModalEntryPointPressableTrigger>
	);
};
